import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PageGlobalParameters} from '@models/pages/page-global-parameters';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {MenuItem} from '@models/sidebar/menu-item';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import {takeUntil} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {Machine} from '@models/machine/machine';
import {MachineService} from '@services/administration/machine/machine.service';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {TemplatePagesService} from '@services/left-menu/page/template-user-defined-page/template-pages.service';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'scitis-status-event-page-container',
    templateUrl: './status-event-page-container.component.html',
    styleUrls: ['./status-event-page-container.component.scss'],
    standalone: false
})
export class StatusEventPageContainerComponent implements OnInit {

  public pageGlobalParameters: PageGlobalParameters = new PageGlobalParameters();
  public routes: MenuItem[] = [];
  public isAdminOrHigher: boolean = false;
  public isAdminOrLower: boolean = false;
  public isTemplatePage: boolean = false;
  public machineCategory: string | null = null;
  public machineTag: string | null = null;
  public statusEventPageConfiguration: StatusEventPage = new StatusEventPage(-1, '');
  public syncTemplatePage: boolean = false;
  public isLoading: boolean = false;
  private CURRENT_PAGE_NAME: string = 'StatusEventPage';
  private $destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private activatedRoute: ActivatedRoute,
              private translate: TranslateService,
              private pagesHelperService: PagesHelperService,
              private breadcrumbService: BreadcrumbService,
              private machineService: MachineService,
              private notification: NotificationService,
              private loadingIndicator: LoadingIndicatorService,
              private statusEventPageService: StatusEventPageService,
              private templatePagesService: TemplatePagesService,
              private authenticationService: FirebaseAuthenticationService) {
  }

  ngOnInit(): void {
    this.pageGlobalParameters = this.pagesHelperService.getPageDetailsFromUrl(this.activatedRoute.snapshot.paramMap);
    this.generateRoutes(this.pageGlobalParameters.associatedMenuItem);
    this.determineIfTemplatePage();
    this.loadStatusEventPageConfiguration(this.pageGlobalParameters, this.isTemplatePage);
    this.getMachineDetails(this.pageGlobalParameters.machineId);
    this.authenticationService.getLoggedUserDetails().subscribe((user: User) => {
      if (user.parentId) {
        const userRole = new Role(user);
        this.isAdminOrHigher = userRole.isAdminRole || userRole.isTechAdminRole || userRole.isSuperAdminRole;
        this.isAdminOrLower = userRole.isAdminRole || userRole.isDistributorRole || userRole.isAnalyticsRole || userRole.isUserRole || userRole.isViewerRole;
      }
    }, (error) => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
    });
  }

  private generateRoutes(menuItem: MenuItem) {
    this.routes = this.breadcrumbService.generateRoutes(menuItem);
  }

  private determineIfTemplatePage() {
    this.isTemplatePage = this.activatedRoute.snapshot.data.isTemplate;
  }

  private getMachineDetails(machineId: string) {
    if (!this.isTemplatePage) {
      return;
    }

    if (!machineId || String(machineId).isNullOrWhitespace()) {
      this.notification.error('Error getting machine configuration.', null, this.CURRENT_PAGE_NAME, true);
      return;
    }
    this.isLoading = true;
    this.machineService.getMachineConfiguration(Number(machineId))
    .pipe(takeUntil(this.$destroy))
    .subscribe((machine: Machine) => {
      this.machineCategory = machine.category;
      this.machineTag = machine.machineTag;
      this.isLoading = false;
    }, error => {
      this.notification.error('Error getting machine configuration.', error, this.CURRENT_PAGE_NAME, true);
      this.isLoading = false;
    });
  }

  private loadStatusEventPageConfiguration(pageGlobalParameters: PageGlobalParameters, isTemplatePage: boolean) {
    const machineId: string | null = pageGlobalParameters.machineId;
    const pageId: string | null = pageGlobalParameters.pageId;
    const pageName: string = pageGlobalParameters.pageName;

    if (!pageId || !machineId) {
      return;
    }
    this.isLoading = true;
    this.loadingIndicator.show();
    let observable: Observable<any>;
    if (isTemplatePage) {
      observable = this.templatePagesService.getPageDetails(machineId, pageId, true);
    } else {
      observable = this.statusEventPageService.getStatusEventPage(machineId, pageId);
    }
    observable.subscribe((page) => {
      [this.statusEventPageConfiguration, this.syncTemplatePage] = this.templatePagesService.parseTemplatePageToStatusPage(page, machineId, isTemplatePage);
      this.loadingIndicator.hide();
      this.isLoading = false;
    }, (error) => {
      let message = this.translate.instant('There was an error loading status page %page configuration');
      message = message.replaceAll('%page', pageName.toBold());
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
      this.isLoading = false;
    });
  }


}
